import * as React from 'react';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';
import {
  createWebhookRequested,
  deleteWebhookRequested,
  fetchWebhooksRequested,
  getWebhooks,
  secretModalClosed,
} from '@packages/systems/designer/webhooks';
import type {WebhookResponse} from '@packages/systems/designer/site-data';

interface UseWebhookSettingsReturn {
  webhooks: WebhookResponse[];
  addWebhook: (url: string) => void;
  removeWebhook: (url: string) => void;
  closeSecretModal: () => void;
  webhookWithSecret: WebhookResponse | undefined;
}

export const useWebhookSettings = (): UseWebhookSettingsReturn => {
  const dispatch = useDispatch();

  // Get webhooks from the designer store
  const siteWebhooks = useDesignerSelector(getWebhooks);

  // Get the webhook with the secret key
  const webhookWithSecret = React.useMemo(() => {
    return siteWebhooks.find((webhook) => webhook.secretKey);
  }, [siteWebhooks]);

  // Filter webhooks for form submissions only
  const webhooks = React.useMemo(() => {
    return siteWebhooks.filter(
      (webhook) => webhook.triggerType === 'form_submission'
    );
  }, [siteWebhooks]);

  const removeWebhook = React.useCallback(
    (url: string) => {
      // Find the webhook by URL to get its ID
      const webhookToDelete = webhooks.find((webhook) => webhook.url === url);
      if (webhookToDelete) {
        dispatch(deleteWebhookRequested({webhookId: webhookToDelete._id}));
      }
    },
    [dispatch, webhooks]
  );

  const addWebhook = React.useCallback(
    (url: string) => {
      dispatch(
        createWebhookRequested({
          url,
          triggerType: 'form_submission',
          version: 'v2',
        })
      );
    },
    [dispatch]
  );

  const closeSecretModal = React.useCallback(() => {
    dispatch(secretModalClosed());
  }, [dispatch]);

  // Fetch webhooks on mount
  React.useEffect(() => {
    dispatch(fetchWebhooksRequested());
  }, [dispatch]);

  return {
    webhooks,
    addWebhook,
    removeWebhook,
    closeSecretModal,
    webhookWithSecret,
  };
};
